import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const FilterControls = ({ onFiltersChange, totalResults }) => {
  const [filters, setFilters] = useState({
    registry: '',
    methodology: '',
    vintage: '',
    ccpStatus: '',
    search: ''
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const registryOptions = [
    { value: '', label: 'All Registries' },
    { value: 'verra', label: 'Verra (VCS)' },
    { value: 'gold-standard', label: 'Gold Standard' },
    { value: 'acr', label: 'American Carbon Registry' },
    { value: 'art', label: 'Architecture for REDD+ Transactions' },
    { value: 'gcc', label: 'Global Carbon Council' },
    { value: 'cad-trust', label: 'CAD Trust' }
  ];

  const methodologyOptions = [
    { value: '', label: 'All Methodologies' },
    { value: 'forestry', label: 'Forestry & Land Use' },
    { value: 'renewable', label: 'Renewable Energy' },
    { value: 'energy-efficiency', label: 'Energy Efficiency' },
    { value: 'waste', label: 'Waste Management' },
    { value: 'transport', label: 'Transportation' },
    { value: 'industrial', label: 'Industrial Processes' }
  ];

  const vintageOptions = [
    { value: '', label: 'All Vintages' },
    { value: '2024', label: '2024' },
    { value: '2023', label: '2023' },
    { value: '2022', label: '2022' },
    { value: '2021', label: '2021' },
    { value: '2020', label: '2020' },
    { value: 'older', label: '2019 and earlier' }
  ];

  const ccpStatusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'CCP-Approved', label: 'CCP-Approved' },
    { value: 'Under Review', label: 'Under Review' },
    { value: 'Non-Compliant', label: 'Non-Compliant' },
    { value: 'Pending', label: 'Pending Assessment' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      registry: '',
      methodology: '',
      vintage: '',
      ccpStatus: '',
      search: ''
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const hasActiveFilters = Object.values(filters)?.some(value => value !== '');

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <h3 className="text-sm font-medium text-card-foreground">Filters</h3>
          {totalResults !== undefined && (
            <span className="text-sm text-muted-foreground">
              {totalResults?.toLocaleString()} results
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={clearAllFilters}
            >
              Clear All
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? 'Less' : 'More'} Filters
          </Button>
        </div>
      </div>
      {/* Search Bar - Always Visible */}
      <div className="mb-4">
        <Input
          type="search"
          placeholder="Search by serial number, project name, or location..."
          value={filters?.search}
          onChange={(e) => handleFilterChange('search', e?.target?.value)}
          className="w-full"
        />
      </div>
      {/* Basic Filters - Always Visible */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Select
          label="Registry"
          options={registryOptions}
          value={filters?.registry}
          onChange={(value) => handleFilterChange('registry', value)}
        />
        
        <Select
          label="CCP Status"
          options={ccpStatusOptions}
          value={filters?.ccpStatus}
          onChange={(value) => handleFilterChange('ccpStatus', value)}
        />
        
        <Select
          label="Vintage Year"
          options={vintageOptions}
          value={filters?.vintage}
          onChange={(value) => handleFilterChange('vintage', value)}
        />
        
        <Select
          label="Methodology"
          options={methodologyOptions}
          value={filters?.methodology}
          onChange={(value) => handleFilterChange('methodology', value)}
        />
      </div>
      {/* Advanced Filters - Expandable */}
      {isExpanded && (
        <div className="border-t border-border pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Integrity Score Range
              </label>
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  placeholder="Min"
                  min="0"
                  max="100"
                  className="w-20"
                />
                <span className="text-muted-foreground">to</span>
                <Input
                  type="number"
                  placeholder="Max"
                  min="0"
                  max="100"
                  className="w-20"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Quantity Range (tCO2e)
              </label>
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  placeholder="Min"
                  className="w-24"
                />
                <span className="text-muted-foreground">to</span>
                <Input
                  type="number"
                  placeholder="Max"
                  className="w-24"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Article 6 Status
              </label>
              <Select
                options={[
                  { value: '', label: 'All' },
                  { value: 'eligible', label: 'Article 6 Eligible' },
                  { value: 'authorized', label: 'Host Country Authorized' },
                  { value: 'not-applicable', label: 'Not Applicable' }
                ]}
                value=""
                onChange={() => {}}
              />
            </div>
          </div>
        </div>
      )}
      {/* Active Filter Tags */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
          {Object.entries(filters)?.map(([key, value]) => {
            if (!value) return null;
            
            const getFilterLabel = (key, value) => {
              switch (key) {
                case 'registry':
                  return registryOptions?.find(opt => opt?.value === value)?.label || value;
                case 'methodology':
                  return methodologyOptions?.find(opt => opt?.value === value)?.label || value;
                case 'vintage':
                  return vintageOptions?.find(opt => opt?.value === value)?.label || value;
                case 'ccpStatus':
                  return ccpStatusOptions?.find(opt => opt?.value === value)?.label || value;
                case 'search':
                  return `Search: "${value}"`;
                default:
                  return value;
              }
            };

            return (
              <span
                key={key}
                className="inline-flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs"
              >
                <span>{getFilterLabel(key, value)}</span>
                <button
                  onClick={() => handleFilterChange(key, '')}
                  className="hover:bg-primary/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FilterControls;